import { z } from 'zod';
import { cardDetailSchema } from './card-detail';
import { checklistListSchema } from './checklist';
import { COMMENT_PAGE_SIZE_MAX, commentStreamReadSchema } from './comment';
import { ulidSchema } from './id';

/**
 * `POST /cards/batch-read` — several card backs, each with its checklists and
 * the head (or, with `after`, the new tail) of its comment stream, in one
 * read-only request. Owner-directed, no requirement backs it; the binding
 * design is `docs/notes/batch-reads.md`, "The batch route".
 *
 * ⚠ **Each entry EQUALS the single reads for the same identity** —
 * `card` is `GET /cards/:id`, `checklists` is `GET /cards/:id/checklists`, and
 * `comments` is `GET /cards/:id/comments?limit=<comments>[&after=<after>]`. So
 * every entry member below is the single route's own schema, reused rather
 * than restated: a field one of those routes gains reaches the batch without a
 * line moving here, and the two can never serve one card in two shapes.
 *
 * ⚠ **A POST, and a `read` row on the connected-app allowlist anyway.** The
 * allowlist's `access` is per row, not per method, and nothing in the auth path
 * ties POST to `write` (the note's "Read-only, measured"). The body is a POST
 * body only because twenty ids with an optional anchor each do not fit a query
 * string well.
 */

/** The most cards one batch may name. The brief's p90 is four, its max seventeen. */
export const CARD_BATCH_READ_MAX_CARDS = 20;

/** Comments per card when the body names no `comments`. */
export const CARD_BATCH_READ_COMMENTS_DEFAULT = 10;

/** One requested card: its id and, optionally, the comment to read after. */
export const cardBatchReadRequestSchema = z.strictObject({
  id: ulidSchema,
  after: ulidSchema
    .optional()
    .describe(
      'A comment id on this card: return only comments created after it, oldest ' +
        'first, plus older comments edited since (GET /cards/:id/comments?after=).',
    ),
});

export type CardBatchReadRequest = z.infer<typeof cardBatchReadRequestSchema>;

/**
 * The request body. Strict at both levels, so a guessed key is a 422 rather
 * than ignored.
 *
 * ⚠ **The two cross-field rules are a `superRefine`, which is safe HERE and
 * nowhere upstream of a `.shape` reader.** Nothing extends or `partial()`s this
 * schema; the MCP tool declares its own input and normalises to this body, so a
 * refinement cannot be dropped silently on the way. The rules:
 *
 *   - **A duplicate `id` is a 422**, named at its index — a caller bug, and it
 *     would otherwise be ambiguous which of two `after`s applies.
 *   - **`after` with `comments: 0` is a 422** — the caller asked for no
 *     comments and named a place to read them from.
 */
export const cardBatchReadBodySchema = z
  .strictObject({
    cards: z.array(cardBatchReadRequestSchema).min(1).max(CARD_BATCH_READ_MAX_CARDS),
    comments: z
      .number()
      .int()
      .min(0)
      .max(COMMENT_PAGE_SIZE_MAX)
      .default(CARD_BATCH_READ_COMMENTS_DEFAULT)
      .describe(
        'Comments per card, 0 to 100, default 10. 0 omits the comments key from ' +
          'every entry: the batch is then several GET /cards/:id reads.',
      ),
  })
  .superRefine((body, context) => {
    const seen = new Set<string>();
    for (const [index, card] of body.cards.entries()) {
      if (seen.has(card.id)) {
        context.addIssue({
          code: 'custom',
          path: ['cards', index, 'id'],
          message: 'Each card may be named once in a batch.',
        });
      }
      seen.add(card.id);
      if (card.after !== undefined && body.comments === 0) {
        context.addIssue({
          code: 'custom',
          path: ['cards', index, 'after'],
          message: 'after cannot be combined with comments: 0.',
        });
      }
    }
  });

export type CardBatchReadBody = z.infer<typeof cardBatchReadBodySchema>;

/**
 * A card the caller can read, whole.
 *
 * ⚠ **`comments` is OPTIONAL and absent exactly when the body said
 * `comments: 0`.** An empty stream there would claim `latestCommentId: null` —
 * "this card has no comments" — about a stream nobody read.
 *
 * ⚠ `card` and `checklists` are REQUIRED, and must stay so: the response union
 * below is resolved member by member, and a found entry is told apart from a
 * not-found one only by carrying them.
 */
export const cardBatchReadFoundSchema = z.object({
  id: ulidSchema,
  card: cardDetailSchema,
  checklists: checklistListSchema,
  comments: commentStreamReadSchema.optional(),
});

export type CardBatchReadFound = z.infer<typeof cardBatchReadFoundSchema>;

/**
 * A card the caller cannot read, carrying the server's fused 404: unknown,
 * deleted, an inbox card, or on a board invisible to this caller —
 * indistinguishable, as the single reads make them. It does not fail the other
 * entries; each entry is still whole or absent, never half-built.
 */
export const cardBatchReadNotFoundSchema = z.object({
  id: ulidSchema,
  error: z.literal('NOT_FOUND'),
});

export type CardBatchReadNotFound = z.infer<typeof cardBatchReadNotFoundSchema>;

export const cardBatchReadEntrySchema = z.union([
  cardBatchReadFoundSchema,
  cardBatchReadNotFoundSchema,
]);

export type CardBatchReadEntry = z.infer<typeof cardBatchReadEntrySchema>;

/** The 200: one entry per requested card, in input order. */
export const cardBatchReadSchema = z.object({
  cards: z.array(cardBatchReadEntrySchema),
});

export type CardBatchRead = z.infer<typeof cardBatchReadSchema>;
